/**
 * Build time slot options from field.time_picker_settings (block checkout).
 */

function parseTimeToMinutes(timeStr) {
    if (!timeStr || typeof timeStr !== 'string') {
        return null;
    }
    const trimmed = timeStr.trim();
    const match12 = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match12) {
        let hours = parseInt(match12[1], 10);
        const minutes = parseInt(match12[2], 10);
        const period = match12[3].toUpperCase();
        if (period === 'PM' && hours < 12) {
            hours += 12;
        }
        if (period === 'AM' && hours === 12) {
            hours = 0;
        }
        return hours * 60 + minutes;
    }
    const match24 = trimmed.match(/^(\d{1,2}):(\d{2})$/);
    if (match24) {
        return parseInt(match24[1], 10) * 60 + parseInt(match24[2], 10);
    }
    return null;
}

function parseLeadTimeToMinutes(leadStr) {
    if (!leadStr || typeof leadStr !== 'string') {
        return 0;
    }
    let total = 0;
    const hMatch = leadStr.match(/(\d+)\s*h/i);
    const mMatch = leadStr.match(/(\d+)\s*m/i);
    if (hMatch) {
        total += parseInt(hMatch[1], 10) * 60;
    }
    if (mMatch) {
        total += parseInt(mMatch[1], 10);
    }
    return total;
}

function formatMinutes(minutes, format) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (format === '24') {
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
    const period = h >= 12 ? 'PM' : 'AM';
    let h12 = h % 12;
    if (h12 === 0) {
        h12 = 12;
    }
    return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
}

function parseDisabledRanges(disableStr) {
    if (!disableStr) {
        return [];
    }
    return disableStr.split(',').map((range) => {
        const parts = range.trim().split('-');
        if (parts.length !== 2) {
            return null;
        }
        const start = parseTimeToMinutes(parts[0].trim());
        const end = parseTimeToMinutes(parts[1].trim());
        if (start === null || end === null) {
            return null;
        }
        return { start, end };
    }).filter(Boolean);
}

function isSlotDisabled(minutes, disabledRanges) {
    return disabledRanges.some((range) => minutes >= range.start && minutes < range.end);
}

function parseDateValue(dateStr) {
    if (!dateStr) {
        return null;
    }
    if (typeof dateStr === 'string' && dateStr.indexOf('T') !== -1) {
        dateStr = dateStr.split('T')[0];
    }
    let d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
        return d;
    }
    const parts = dateStr.split(/[\/\-\.]/);
    if (parts.length === 3) {
        d = new Date(parts[0], parts[1] - 1, parts[2]);
        if (!isNaN(d.getTime())) {
            return d;
        }
        d = new Date(parts[2], parts[0] - 1, parts[1]);
        if (!isNaN(d.getTime())) {
            return d;
        }
    }
    return null;
}

function isToday(dateObj) {
    const now = new Date();
    return dateObj.getFullYear() === now.getFullYear() &&
        dateObj.getMonth() === now.getMonth() &&
        dateObj.getDate() === now.getDate();
}

function isPastDate(dateObj) {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const check = new Date(dateObj);
    check.setHours(0, 0, 0, 0);
    return check < now;
}

export function isValidTimePickerTime(time, timeFormat = '12') {
    const value = String(time || '').trim();
    if (!value) {
        return true;
    }
    if (timeFormat === '24') {
        const match24 = value.match(/^(\d{1,2}):(\d{2})$/);
        return !!match24 && parseInt(match24[1], 10) <= 23 && parseInt(match24[2], 10) <= 59;
    }
    const match12 = value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    return !!match12 && parseInt(match12[1], 10) >= 1 && parseInt(match12[1], 10) <= 12 && parseInt(match12[2], 10) <= 59;
}

export function isValidTimePickerLeadTime(lead) {
    const value = String(lead || '').trim();
    if (!value) {
        return true;
    }
    const match = value.match(/^(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?$/i);
    return !!match && (!!match[1] || !!match[2]);
}

export function isValidTimePickerDisableSlots(slots, timeFormat = '12') {
    const value = String(slots || '').trim();
    if (!value) {
        return true;
    }
    return value.split(',').every((range) => {
        const parts = range.trim().split('-').map((part) => part.trim());
        return parts.length === 2 && isValidTimePickerTime(parts[0], timeFormat) && isValidTimePickerTime(parts[1], timeFormat);
    });
}

export function filterValidTimePickerSettings(settings = {}) {
    const timeFormat = settings.time_format === '24' ? '24' : '12';
    const minTime = String(settings.min_time || '').trim();
    const maxTime = String(settings.max_time || '').trim();
    const startTime = String(settings.start_time || '').trim();
    const disableSlots = String(settings.disable_time_slots || '').trim();
    const timeStep = parseInt(settings.time_step, 10);

    return {
        linked_date: settings.linked_date || '',
        min_time: isValidTimePickerTime(minTime, timeFormat) ? minTime : '',
        max_time: isValidTimePickerTime(maxTime, timeFormat) ? maxTime : '',
        start_time: isValidTimePickerLeadTime(startTime) ? startTime : '',
        time_step: (!Number.isNaN(timeStep) && timeStep >= 1 && timeStep <= 1440) ? String(timeStep) : '30',
        time_format: timeFormat,
        disable_time_slots: isValidTimePickerDisableSlots(disableSlots, timeFormat) ? disableSlots : '',
    };
}

export function getTimepickerConfig(field) {
    const settings = filterValidTimePickerSettings(field.time_picker_settings || {});
    return {
        linkedDate: settings.linked_date || '',
        minTime: settings.min_time || '',
        maxTime: settings.max_time || '',
        startTime: settings.start_time || '',
        timeStep: parseInt(settings.time_step, 10) || 30,
        timeFormat: settings.time_format === '24' ? '24' : '12',
        disableTimeSlots: settings.disable_time_slots || '',
    };
}

/** Config shape expected by fme-ccfw-timepicker.js (matches PHP get_time_picker_config). */
export function getTimepickerInitConfig(field) {
    return getTimepickerConfig(field);
}

/**
 * Repeat suffix for a rendered field name (e.g. billing_time_2 → _2).
 */
export function getFieldRepeatSuffix(fieldName, baseFieldName) {
    if (!fieldName || !baseFieldName || fieldName === baseFieldName) {
        return '';
    }
    if (fieldName.startsWith(`${baseFieldName}_`)) {
        return fieldName.slice(baseFieldName.length);
    }
    return '';
}

/**
 * Resolve linked date field name (handles repeat suffixes and cross-block keys).
 */
export function resolveLinkedDateFieldName(linkedDate, timeFieldName, timeFieldBase, allFieldValues = {}) {
    if (!linkedDate) {
        return '';
    }

    if (allFieldValues[linkedDate]) {
        return linkedDate;
    }

    const suffix = getFieldRepeatSuffix(timeFieldName, timeFieldBase);
    if (suffix) {
        const suffixedName = `${linkedDate}${suffix}`;
        if (allFieldValues[suffixedName] !== undefined && allFieldValues[suffixedName] !== '') {
            return suffixedName;
        }
    }

    return linkedDate;
}

/**
 * Read linked date value from merged field values or DOM (block + classic parity).
 */
export function resolveLinkedDateValue(linkedDate, timeFieldName, timeFieldBase, allFieldValues = {}) {
    if (!linkedDate) {
        return null;
    }

    const resolvedName = resolveLinkedDateFieldName(linkedDate, timeFieldName, timeFieldBase, allFieldValues);
    const fromState = allFieldValues[resolvedName];
    if (fromState !== undefined && fromState !== null && fromState !== '') {
        return String(fromState).trim();
    }

    if (typeof window !== 'undefined' && window.jQuery) {
        const $el = window.jQuery(`[name="${resolvedName}"], #${resolvedName}`);
        if ($el.length) {
            const domVal = ($el.val() || '').trim();
            if (domVal) {
                return domVal;
            }
        }
    }

    return '';
}

export function generateTimeSlots(config, linkedDateValue = null) {
    const minMinutes = parseTimeToMinutes(config.minTime) ?? 0;
    const maxMinutes = parseTimeToMinutes(config.maxTime) ?? (24 * 60 - 1);
    const step = config.timeStep || 30;
    const format = config.timeFormat || '12';
    const disabledRanges = parseDisabledRanges(config.disableTimeSlots || '');
    const leadMinutes = parseLeadTimeToMinutes(config.startTime || '');
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    let earliestMinutes = minMinutes;

    if (config.linkedDate) {
        const dateStr = linkedDateValue;
        if (!dateStr) {
            return [];
        }
        const dateObj = parseDateValue(dateStr);
        if (!dateObj) {
            return [];
        }
        if (isPastDate(dateObj)) {
            return [];
        }
        if (isToday(dateObj)) {
            earliestMinutes = Math.max(minMinutes, currentMinutes + leadMinutes);
            earliestMinutes = Math.ceil(earliestMinutes / step) * step;
        }
    } else if (leadMinutes > 0) {
        earliestMinutes = Math.max(minMinutes, currentMinutes + leadMinutes);
        earliestMinutes = Math.ceil(earliestMinutes / step) * step;
    }

    const slots = [];
    for (let m = earliestMinutes; m <= maxMinutes; m += step) {
        if (!isSlotDisabled(m, disabledRanges)) {
            const label = formatMinutes(m, format);
            slots.push({ value: label, label });
        }
    }
    return slots;
}
